import { MARKETPLACE_SUMMARY_FIELDS } from '../constants';

// Rows the Marketplace Summary table itself never renders — keep the audit diff in lockstep
// with what the user actually sees on screen.
const HIDDEN_ROWS = ['Receivables', 'Remittance'];

// Rows the Marketplace Summary table renders in bold — mirror that in the audit preview.
const BOLD_ROWS = ['Net Product Sales', 'Income (Gross Sales)', 'Total Expenses', 'Net Income (Net Sales)'];
const isBoldName = (name) => BOLD_ROWS.includes(String(name ?? '').trim());

// The columns visible for the chosen currency. Mirrors MarketplaceTable.getTableHeaders so the
// preview compares exactly the cells shown in the table.
const columnsForCurrency = (currency) =>
    MARKETPLACE_SUMMARY_FIELDS.filter(({ id }) => {
        if (id === 'name') return true;
        if (currency === 'CAD') return id.includes('_ca');
        if (currency === 'USD') return id.includes('_com') || id.includes('_us');
        return true; // Consolidated / Consolidated CAD show every column
    });

const isNumericLike = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'number') return true;
    return !isNaN(parseFloat(String(v).replace(/,/g, '')));
};

// Money rounded to 2 decimals, strings trimmed — so 5, "5.00" and "5.0" all compare equal.
const normalizeForCompare = (v) => {
    if (v === null || v === undefined) return '';
    if (isNumericLike(v)) return parseFloat(String(v).replace(/,/g, '')).toFixed(2);
    return String(v).trim();
};

const visibleRows = (rows) =>
    (Array.isArray(rows) ? rows : []).filter(
        (r) => r && r.name && !HIDDEN_ROWS.includes(r.name)
    );

/**
 * Builds an audit diff between the currently-displayed marketplace summary (oldRows) and the
 * recomputed-but-unsaved refresh result (newRows). The returned shape matches what
 * LedgerAuditPreviewDialog consumes: { sections: [...], counts: {changed, added, removed} }.
 *
 * @param {string} currency  Selected currency — selects which columns are compared/shown.
 * @param {Array}  oldRows   Current summary rows ({ name, amazon_ca, ... }).
 * @param {Array}  newRows   Preview summary rows (same shape).
 */
export const buildMarketplaceDiff = (currency, oldRows = [], newRows = []) => {
    const cellConfig = columnsForCurrency(currency);
    const compareFields = cellConfig.map((c) => c.id).filter((id) => id !== 'name');

    const oldList = visibleRows(oldRows);
    const newList = visibleRows(newRows);

    const oldById = new Map();
    oldList.forEach((r) => oldById.set(normalizeForCompare(r.name), r));
    const newById = new Map();
    newList.forEach((r) => newById.set(normalizeForCompare(r.name), r));

    const buildFields = (oldRow, newRow) => {
        const fields = {};
        cellConfig.forEach(({ id }) => {
            const oldVal = oldRow ? oldRow[id] : undefined;
            const newVal = newRow ? newRow[id] : undefined;
            const changed = compareFields.includes(id) && !!oldRow && !!newRow
                && normalizeForCompare(oldVal) !== normalizeForCompare(newVal);
            fields[id] = { old: oldVal, new: newVal, changed };
        });
        return fields;
    };

    const rows = [];
    // Removed + changed + unchanged in the OLD order, then appended additions.
    oldList.forEach((oldRow) => {
        const id = normalizeForCompare(oldRow.name);
        const newRow = newById.get(id);
        if (!newRow) {
            rows.push({ id, name: oldRow.name, bold: isBoldName(oldRow.name), status: 'removed', fields: buildFields(oldRow, null) });
        } else {
            const fields = buildFields(oldRow, newRow);
            const changed = Object.values(fields).some((f) => f.changed);
            rows.push({ id, name: newRow.name, bold: isBoldName(newRow.name), status: changed ? 'changed' : 'unchanged', fields });
        }
    });
    newList.forEach((newRow) => {
        const id = normalizeForCompare(newRow.name);
        if (!oldById.has(id)) {
            rows.push({ id, name: newRow.name, bold: isBoldName(newRow.name), status: 'added', fields: buildFields(null, newRow) });
        }
    });

    const counts = rows.reduce((acc, r) => {
        if (r.status !== 'unchanged') acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
    }, { changed: 0, added: 0, removed: 0 });

    const section = {
        key: 'marketplace',
        title: 'Marketplace Summary',
        cellConfig,
        idField: 'name',
        mapName: false,
        rows,
        counts,
    };

    return { sections: [section], counts: { ...counts } };
};

// Money display matching the Marketplace Summary table cells (2 decimals + thousands separators).
export const displayMarketplaceValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (isNumericLike(value)) {
        return parseFloat(String(value).replace(/,/g, ''))
            .toFixed(2)
            .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
    }
    return String(value);
};
